import { useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"

interface BulkDeleteParams {
  ids: string[]
}

interface BulkUpdateParams {
  ids: string[]
  changes: Record<string, unknown>
}

interface BulkTagParams {
  ids: string[]
  tag_names: string[]
}

interface BulkDeleteResult {
  deleted: number
}

interface BulkUpdateResult {
  updated: number
}

interface BulkTagResult {
  tagged: number
}

type EntityType = "companies" | "contacts" | "deals" | "tasks" | "service_tickets" | "invoices"

function getQueryKeys(entity: EntityType): string[][] {
  switch (entity) {
    case "companies":
      return [["companies"]]
    case "contacts":
      return [["contacts"]]
    case "deals":
      return [["deals"], ["deals_board"]]
    case "tasks":
      return [["tasks"]]
    case "service_tickets":
      return [["service_tickets"], ["service_tickets_board"]]
    case "invoices":
      return [["invoices"]]
  }
}

export function useBulkDelete(entity: EntityType) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: BulkDeleteParams) =>
      api.post<BulkDeleteResult>(`/api/v1/${entity}/bulk_delete`, params),
    onSuccess: () => {
      getQueryKeys(entity).forEach((key) => {
        qc.invalidateQueries({ queryKey: key })
      })
    },
  })
}

export function useBulkUpdate(entity: EntityType) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: BulkUpdateParams) =>
      api.post<BulkUpdateResult>(`/api/v1/${entity}/bulk_update`, params),
    onSuccess: () => {
      getQueryKeys(entity).forEach((key) => {
        qc.invalidateQueries({ queryKey: key })
      })
    },
  })
}

export function useBulkTag(entity: EntityType) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: BulkTagParams) =>
      api.post<BulkTagResult>(`/api/v1/${entity}/bulk_tag`, params),
    onSuccess: () => {
      getQueryKeys(entity).forEach((key) => {
        qc.invalidateQueries({ queryKey: key })
      })
    },
  })
}
